import { Button, Table, Tags } from "../ui";
import { formatSar } from "../../utils/billingMoney";
import { labelFor } from "./admin/billingAdminI18n";

const statusVariant = {
  paid: "success",
  partial: "warning",
  unpaid: "warning",
  pending_review: "info",
  succeeded: "success",
  failed: "danger",
  canceled: "neutral",
};

const formatDate = (value, lang = "ar") => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(value));
};

const RestaurantInvoiceDetailsModal = ({
  invoice,
  lang = "ar",
  text,
  onClose,
  onUpload,
  onOpenReceipt,
}) => {
  const customerText = text.customer;
  const itemColumns = [
    {
      key: "type",
      label: text.invoices.type,
      render: (item) => labelFor(text.types, item.type),
    },
    {
      key: "description",
      label: text.invoices.descriptionLabel,
      render: (item) => item.descriptionEn || item.descriptionAr || "-",
    },
    { key: "quantity", label: text.invoices.quantity },
    {
      key: "unitPriceMinor",
      label: text.invoices.unit,
      render: (item) => formatSar(item.unitPriceMinor, lang),
    },
    {
      key: "subtotalMinor",
      label: text.common.subtotal,
      render: (item) => formatSar(item.subtotalMinor, lang),
    },
    {
      key: "discountMinor",
      label: text.common.discount,
      render: (item) => formatSar(item.discountMinor || 0, lang),
    },
    {
      key: "taxableAmountMinor",
      label: text.common.taxable,
      render: (item) => formatSar(item.taxableAmountMinor, lang),
    },
    {
      key: "vatAmountMinor",
      label: text.common.vat,
      render: (item) => formatSar(item.vatAmountMinor, lang),
    },
    {
      key: "totalMinor",
      label: text.common.total,
      render: (item) => formatSar(item.totalMinor, lang),
    },
  ];

  const paymentColumns = [
    {
      key: "amountMinor",
      label: text.common.amount,
      render: (payment) => formatSar(payment.amountMinor, lang),
    },
    {
      key: "method",
      label: text.common.method,
      render: (payment) => labelFor(text.methods, payment.method),
    },
    {
      key: "reference",
      label: text.common.reference,
      render: (payment) => payment.reference || "-",
    },
    {
      key: "status",
      label: text.common.status,
      render: (payment) => (
        <Tags variant={statusVariant[payment.status] || "neutral"}>
          {labelFor(text.statuses, payment.status)}
        </Tags>
      ),
    },
    {
      key: "createdAt",
      label: text.payments.submitted,
      render: (payment) => formatDate(payment.createdAt, lang),
    },
    {
      key: "reviewedAt",
      label: customerText.reviewed,
      render: (payment) => formatDate(payment.reviewedAt, lang),
    },
    {
      key: "failureReason",
      label: text.common.reason,
      render: (payment) => payment.failureReason || "-",
    },
    {
      key: "receipt",
      label: "",
      render: (payment) =>
        payment.receiptFile ? (
          <Button
            size="sm"
            appearance="outline"
            onClick={() => onOpenReceipt(payment)}
          >
            {text.payments.receipt}
          </Button>
        ) : (
          "-"
        ),
    },
  ];

  const snapshot = invoice.pricingSnapshot || {};
  const billing = invoice.billingDetailsSnapshot || {};

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal restaurant-invoice-modal">
        <h3>{invoice.invoiceNumber || text.invoices.draft}</h3>
        <p>
          {formatDate(invoice.periodStart, lang)} -{" "}
          {formatDate(invoice.periodEnd, lang)}
        </p>
        <div className="billing-version-groups">
          <div>
            <span>{text.common.status}</span>
            <strong>
              <Tags>{labelFor(text.statuses, invoice.status)}</Tags>
            </strong>
          </div>
          <div>
            <span>{text.invoices.payment}</span>
            <strong>
              <Tags variant={statusVariant[invoice.paymentStatus] || "neutral"}>
                {labelFor(text.statuses, invoice.paymentStatus)}
              </Tags>
            </strong>
          </div>
          <div>
            <span>{text.invoices.billingName}</span>
            <strong>
              {billing.billingNameEn || billing.billingNameAr || "-"}
            </strong>
          </div>
          <div>
            <span>{text.invoices.vatNumber}</span>
            <strong>{billing.vatNumber || "-"}</strong>
          </div>
          <div>
            <span>{customerText.planSnapshot}</span>
            <strong>
              {snapshot.planCode || "-"} v{snapshot.planVersion || "-"}
            </strong>
          </div>
          <div>
            <span>{customerText.planPrice}</span>
            <strong>{formatSar(snapshot.basePriceMinor, lang)}</strong>
          </div>
          <div>
            <span>{text.subscriptions.branches}</span>
            <strong>
              {customerText.branchCounts
                .replace("{covered}", snapshot.coveredBranchCount || 0)
                .replace("{extra}", snapshot.extraBranchCount || 0)}
            </strong>
          </div>
          <div>
            <span>{text.invoices.issueDate}</span>
            <strong>{formatDate(invoice.issueDate, lang)}</strong>
          </div>
          <div>
            <span>{text.invoices.dueDate}</span>
            <strong>{formatDate(invoice.dueDate, lang)}</strong>
          </div>
        </div>

        <h3>{customerText.invoiceItems}</h3>
        <Table
          columns={itemColumns}
          data={invoice.items || []}
          emptyText={text.invoices.noItems}
        />

        <div className="billing-preview-box">
          <div>
            <span>{text.common.subtotal}</span>
            <strong>{formatSar(invoice.subtotalMinor, lang)}</strong>
          </div>
          <div>
            <span>{text.common.discount}</span>
            <strong>{formatSar(invoice.discountAmountMinor, lang)}</strong>
          </div>
          <div>
            <span>{text.common.taxable}</span>
            <strong>{formatSar(invoice.taxableAmountMinor, lang)}</strong>
          </div>
          <div>
            <span>{text.common.vat}</span>
            <strong>{formatSar(invoice.vatAmountMinor, lang)}</strong>
          </div>
          <div>
            <span>{text.common.total}</span>
            <strong>{formatSar(invoice.totalMinor, lang)}</strong>
          </div>
          <div>
            <span>{text.common.paid}</span>
            <strong>{formatSar(invoice.paidAmountMinor || 0, lang)}</strong>
          </div>
          <div>
            <span>{text.common.remaining}</span>
            <strong>{formatSar(invoice.balanceDueMinor || 0, lang)}</strong>
          </div>
        </div>

        <h3>{customerText.paymentAttempts}</h3>
        <Table
          columns={paymentColumns}
          data={invoice.payments || []}
          emptyText={customerText.noPaymentAttempts}
        />

        <div className="billing-modal-actions">
          {onUpload && (
            <Button appearance="filled" onClick={onUpload}>
              {customerText.uploadReceipt}
            </Button>
          )}
          <Button appearance="ghost" onClick={onClose}>
            {text.common.close}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantInvoiceDetailsModal;
